import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import Button from "components/basic/button";
import Typography from "components/basic/typography";

import { Booking } from "../../../../types";

const meetingProvider = (url: string): string => {
  if (/meet\.google\./i.test(url)) return "Google Meet";
  if (/zoom\.us/i.test(url)) return "Zoom";
  if (/teams\.(microsoft|live)\./i.test(url)) return "Microsoft Teams";
  if (/webex\./i.test(url)) return "Webex";
  return "Video meeting";
};

// CRM-style conferencing card: a prominent "Join" button + provider + copyable
// link once confirmed, or a "waiting on the host" hint while pending.
const MeetingInfo: React.FC<{ booking: Booking; isCustomer: boolean }> = ({
  booking,
  isCustomer,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");
  const [copied, setCopied] = useState(false);
  const link = booking.meetingLink;
  const isVideo = booking.type === "video";

  if (!isVideo || booking.status === "cancelled") {
    return null;
  }

  if (link) {
    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1.5,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <VideocamIcon
            fontSize="small"
            sx={{ color: theme.palette.primary.main }}
          />
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {meetingProvider(link)}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startAdornment={<VideocamIcon fontSize="small" />}
          onClick={() => window.open(link, "_blank", "noopener,noreferrer")}
        >
          {t("details.join")}
        </Button>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            mt: 1,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          <Typography
            variant="caption"
            style={{
              flex: 1,
              minWidth: 0,
              wordBreak: "break-all",
              color: theme.palette.text.secondary,
            }}
          >
            {link}
          </Typography>
          <IconButton
            size="small"
            title={t("details.copy_link")}
            onClick={() => {
              navigator.clipboard?.writeText(link);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
        {copied && (
          <Typography
            variant="caption"
            style={{ color: theme.palette.success.main }}
          >
            {t("details.link_copied")}
          </Typography>
        )}
      </Box>
    );
  }

  // Video meeting with no link yet.
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        backgroundColor: `${theme.palette.warning.main}14`,
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <ScheduleIcon
        fontSize="small"
        sx={{ color: theme.palette.warning.main, mt: "2px" }}
      />
      <Typography variant="caption">
        {isCustomer
          ? t("details.no_link_customer")
          : t("details.no_link_vendor")}
      </Typography>
    </Stack>
  );
};

export default MeetingInfo;
